import React from 'react';
import styled from 'styled-components';

import Button from '../../../Button';

import {
  StartCall,
  Anonimizer,
  Help,
  Mic,
  Supervisor,
  Withdraw,
} from '@icons/dialer';
import { ValueOf } from '@utils/tsUtils';

export const Types = {
  START_CALL: 'start_call',
  END_CALL: 'end_call',
  HOLD: 'hold',
  MIC: 'mic',
  ANONIMIZER: 'anonimizer',
  HELPER: 'helper',
  SUPERVISOR: 'supervisor',
  WITHDRAW: 'withdraw',
};

interface RoundButtonProps {
  className?: string;
  actionType: ValueOf<typeof Types>;
  active?: boolean;
  pulsing?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

class RoundButton extends React.PureComponent<RoundButtonProps> {
  static defaultProps = {
    className: '',
    actionType: null,
    active: false,
    pulsing: false,
    disabled: false,
  };

  static icon(type) {
    switch (type) {
      case Types.START_CALL:
        return <StartCall />;
      case Types.END_CALL:
        return <StartCall />;
      case Types.HOLD:
        return <StyledHoldIcon />;
      case Types.MIC:
        return <Mic />;
      case Types.ANONIMIZER:
        return <Anonimizer />;
      case Types.HELPER:
        return <Help />;
      case Types.SUPERVISOR:
        return <Supervisor />;
      case Types.WITHDRAW:
        return <Withdraw />;
      default:
        return null;
    }
  }

  render() {
    const { actionType } = this.props;

    return (
      <StyledRoundButton {...this.props}>
        {RoundButton.icon(actionType)}
      </StyledRoundButton>
    );
  }
}

const StyledHoldIcon = styled.div`
  &:before,
  &:after {
    top: 10px;
    left: 14px;
    width: 1px;
    content: '';
    height: 19px;
    position: absolute;
    background-color: #fff;
  }

  &:after {
    margin-left: 8px;
  }
`;

const StyledRoundButton = styled(Button)<{
  actionType: RoundButtonProps['actionType'];
}>`
  width: 40px;
  height: 40px;
  display: flex;
  overflow: visible;
  position: relative;
  border-radius: 100%;
  align-items: center;
  justify-content: center;
  border: 1px solid white;
  background-color: #262729;

  &:hover {
    border-color: white;
  }

  > svg {
    width: 20px;
  }

  ${({ actionType }) =>
    actionType === Types.START_CALL &&
    `
    background-image: linear-gradient(to bottom, #00e04b, #00b0b6);
  `}

  ${({ actionType }) =>
    actionType === Types.END_CALL &&
    `
    background-image: linear-gradient(to bottom, #ff860c, #e5200e);
    
    > svg {
      transform: rotate(-136deg);
    }
  `}

  ${({ actionType }) =>
    [Types.ANONIMIZER, Types.SUPERVISOR].includes(actionType) &&
    `
    margin-top: 1px;
  `}

  ${({ actionType, active }) =>
    [Types.HELPER, Types.ANONIMIZER, Types.SUPERVISOR, Types.WITHDRAW].includes(
      actionType
    ) &&
    `
    svg {
      z-index: 2;
      transform: scale(1.2);
      path:nth-child(1) { stroke: ${active ? 'black' : 'white'}; }
      path:nth-child(2) { fill: ${active ? 'black' : 'white'}; }
    }
  `}

  ${({ actionType, active }) =>
    actionType === Types.HELPER &&
    !active &&
    `
    svg {
      path, circle { stroke: white; }
    }
  `}

  ${({ actionType, active }) =>
    [Types.MIC, Types.SUPERVISOR].includes(actionType) &&
    active &&
    `
    &:before {
      top: 18px;
      left: 5px;
      height: 1px;
      content: '';
      width: 27px;
      background: #000;
      position: absolute;
      transform: rotate(38deg);
    }
  `}

  ${({ actionType, active }) =>
    actionType === Types.MIC &&
    active &&
    `
    svg { rect, path { stroke: black; } }
  `}

  ${({ actionType, active }) =>
    actionType === Types.HOLD &&
    active &&
    `
    div {
      &:before,
      &:after { background: black; }
    }
  `}

  ${({ active }) =>
    active &&
    `
    background-color: white;
  `}

  ${({ disabled }) =>
    disabled &&
    `
    opacity: 0.5;
  `}

  ${({ pulsing }) =>
    pulsing &&
    `
    overflow: visible;
    background-color: transparent;
    border-color: rgba(255,255,255,.4);

    &:before,
    &:after {
      top: 50%;
      left: 50%;
      content: '';
      z-index: 1;
      opacity: 0.5;
      position: absolute;
      border-radius: 100%;
      animation: pulsing 1.5s infinite ease-in-out;
      background-image: linear-gradient(to top, #00e04b, #00b0b6);
    }

    &:before {
      width: 78px;
      height: 78px;
      margin-top: -39px;
      margin-left: -39px;
    }

    &:after {
      width: 112px;
      height: 112px;
      margin-top: -56px;
      margin-left: -56px;
    }

    @keyframes pulsing {
      from {transform: scale(.5);}
    }
  `}
`;

export default RoundButton;
